// src/utils/creditsFormat.js
//
// Display helpers for the Credits dashboard. Every credit/cost field arrives as
// a lossless Decimal STRING on the wire — parse before formatting/arithmetic.
// Display precision (integer credits, 2-decimal USD) is fine for the dashboard;
// we never write these values back, so JS-number rounding is acceptable here.
//
// ⛔ The expiring-credits formatter was removed on 2026-09-23: the backend withdrew
// the expiring-credits amount and its expiry date from the balance payload.
// Do not reintroduce it.

import { getActiveLocale } from '../i18n/locale.js';

const ROME = 'Europe/Rome';
const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Parse a Decimal-string field to a finite Number, or null for null/blank/NaN. */
export function parseDecimal(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Thousands-separated integer credits in the active UI locale (e.g. it-IT "250.000"). null/NaN -> "—". */
export function formatCredits(value) {
  const n = parseDecimal(value);
  if (n == null) return '—';
  return new Intl.NumberFormat(getActiveLocale(), { maximumFractionDigits: 0 }).format(Math.round(n));
}

/** USD currency string (e.g. "$1,830.00"). null/NaN -> null (caller hides UI). */
export function formatMoney(value) {
  const n = parseDecimal(value);
  if (n == null) return null;
  return moneyFmt.format(n);
}

/**
 * A plan or credit-pack PRICE for display: the amount WITHOUT decimals, in the active UI
 * locale ("59", "1.350"). The column is NUMERIC(18,6), so the wire says "59.000000".
 * ⛔ Never decimals — a product decision (2026-09-29); the catalogue holds whole euros.
 * The currency code is the caller's (shown as sent, e.g. "59 EUR"); ⛔ no VAT arithmetic.
 * null/blank/NaN -> null (the caller falls back to the raw value).
 */
export function formatPrice(value) {
  const n = parseDecimal(value);
  if (n == null) return null;
  return new Intl.NumberFormat(getActiveLocale(), { maximumFractionDigits: 0 }).format(n);
}

/**
 * Consumed fraction (0..1) of the period's ALLOWANCE, for the gauge bar.
 * Returns null when there is no plan or a non-positive allowance (untracked).
 *
 * ⛔ The allowance pot is the ONLY one that can be gauged: `purchased_remaining`
 * has no denominator (packs pool, never expire, no ceiling), so a bar over the
 * spendable total would have no maximum to divide by.
 *
 * ⚠️ The two clamps are load-bearing, not defensive noise — both pinches happen
 * in production and both must keep the bar inside the track while the card goes
 * on printing the server's real number beside it:
 *   - consumed > allowance (the last run overshot, so remaining_credits comes
 *     back NEGATIVE) -> 1, i.e. the remaining bar at 0;
 *   - consumed < 0 / remaining > allowance (mid-period downgrade, or a top-up
 *     leaving more open grants than the new allowance) -> 0, i.e. bar at 100%.
 */
export function consumedFraction(consumed, allowance) {
  const c = parseDecimal(consumed);
  const m = parseDecimal(allowance);
  if (c == null || m == null || m <= 0) return null;
  return Math.min(1, Math.max(0, c / m));
}

/** Europe/Rome wall-clock parts of an instant, as numbers. */
function romeParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ROME, year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
  }).formatToParts(date);
  const out = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out;
}

/**
 * True when [start, end) is exactly one Europe/Rome calendar month — i.e. both
 * bounds land on midnight of the 1st and are one month apart. That is the shape
 * the backend returns for a plan with no `period_days`, and for a tenant with no
 * plan or no customer at all.
 *
 * A 30-day rolling window that happens to start on 1 September also matches
 * (1 Sep + 30d == 1 Oct); the month label is the correct label for it anyway.
 */
function isRomeCalendarMonth(start, end) {
  const s = romeParts(start);
  const e = romeParts(end);
  if (s.day !== 1 || s.hour !== 0 || s.minute !== 0) return false;
  if (e.day !== 1 || e.hour !== 0 || e.minute !== 0) return false;
  const months = (e.year - s.year) * 12 + (e.month - s.month);
  return months === 1;
}

/** "Giugno 2026" — the Rome calendar month containing `date`. */
function monthLabel(date) {
  const label = date.toLocaleDateString(getActiveLocale(), {
    month: 'long', year: 'numeric', timeZone: ROME,
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "5 ago – 4 set 2026" — the two bounds verbatim, no arithmetic on them. */
function rangeLabel(start, end) {
  const fmt = new Intl.DateTimeFormat(getActiveLocale(), {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: ROME,
  });
  if (typeof fmt.formatRange === 'function') {
    try { return fmt.formatRange(start, end); } catch { /* older ICU: fall through */ }
  }
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

/**
 * Period label for the balance card, from the response's [period_start,
 * period_end) bounds.
 *
 * ⚠️ The window has THREE shapes and `/credits/balance` carries no `period_days`
 * or `renews` to tell them apart, so the shape is recovered from the bounds:
 *   - Europe/Rome calendar month (plan with no period_days, or no plan/customer)
 *     -> the month name, e.g. "Giugno 2026";
 *   - rolling paid window (period_days + renews, 30 days from started_at) and
 *     fixed trial window (period_days, renews false) -> a date range.
 * Never recompute the boundaries — the server owns them.
 *
 * `periodEnd` is optional: with only a start there is nothing to distinguish the
 * shapes, so we keep the pre-existing calendar-month label, which is still the
 * right answer for that shape (and for an old backend that sends no end).
 *
 * period_start is the UTC instant of a Rome boundary, so we format in the Rome
 * timezone to recover the intended wall-clock date. The month/day names follow
 * the active UI locale; the timezone stays Europe/Rome.
 */
export function formatPeriodLabel(periodStartIso, periodEndIso) {
  if (!periodStartIso) return '';
  const start = new Date(periodStartIso);
  if (Number.isNaN(start.getTime())) return '';

  const end = periodEndIso ? new Date(periodEndIso) : null;
  if (!end || Number.isNaN(end.getTime())) return monthLabel(start);

  return isRomeCalendarMonth(start, end) ? monthLabel(start) : rangeLabel(start, end);
}

/**
 * A single boundary date — "30 giugno 2026". Two callers, both naming a moment
 * the server owns and both reading `period_end`: the "renews on {{date}}" line of
 * a CREDITS_EXHAUSTED refusal, and the neutral "periodo fino al {{date}}" half of
 * the balance card's allowance line. Neither derives the date — this only
 * formats it.
 *
 * ⚠️ As a RENEWAL date it is only ever used for a **rolling** plan. A
 * TRIAL_EXPIRED window is terminal and must never be dated: telling a user their
 * elapsed trial "renews on …" promises something that will not happen. That rule
 * lives at the call site (utils/runRefusal.js), which simply does not ask for a
 * date in that state. The balance card never says "renews" at all: on a paid
 * plan `period_end` is a PREDICTION (credits reappear when the next grant is
 * written), so it states the bound and promises nothing.
 *
 * Formatted in Europe/Rome, like every other period bound here, so the wall
 * clock the server intended is the one the user reads.
 */
export function formatRenewalDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getActiveLocale(), {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: ROME,
  });
}

/**
 * The wall-clock time of an instant («11:30»), in Europe/Rome like `formatRenewalDate` — v204, for the deadline of a
 * payment the bank wants confirmed (3-D Secure), which Stripe sets about 23 hours out, so the day alone is not
 * enough. Empty string for a missing or invalid value.
 */
export function formatRenewalTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(getActiveLocale(), { hour: '2-digit', minute: '2-digit', timeZone: ROME });
}
